import {
  formatCertificateDate,
  normalizeDecimalValue,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import type {
  TStateAidCertificateDto,
  TStateAidFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/types.ts';

const NUMBER_FIELDS = new Set<keyof TStateAidCertificateDto>([
  'apprAmountMmsp',
  'apprAmountGroup',
  'reqAmountMmsp',
  'reqAmountGroup',
  'covidAmountMmsp',
  'covidAmountGroup',
  'cultivatedArea',
]);

const BOOLEAN_FIELDS = new Set<keyof TStateAidCertificateDto>([
  'exportSales',
  'idpStatus',
  'veteranStatus',
  'disabilityStatus',
  'agricultural',
]);

type TStateAidSerializationOptions = {
  includeCovid: boolean;
};

export const mapStateAidFormValuesToDto = (
  values: TStateAidFormValues,
  { includeCovid }: TStateAidSerializationOptions
): TStateAidCertificateDto => {
  const dto: TStateAidCertificateDto = {
    documentDate: formatCertificateDate(values.documentDate) ?? '',
    companyFullName: values.companyFullName,
    companyIdentifier: values.companyIdentifier,
  };
  const dtoValues = dto as Record<string, unknown>;

  (Object.keys(values) as Array<keyof TStateAidFormValues>).forEach((field) => {
    if (
      field === 'documentDate' ||
      field === 'companyFullName' ||
      field === 'companyIdentifier' ||
      field === 'receivedAidQuarters'
    )
      return;
    if (!includeCovid && field.startsWith('covid')) return;

    const dtoField = String(field);
    const value = values[field];

    if (NUMBER_FIELDS.has(dtoField as keyof TStateAidCertificateDto)) {
      const normalized = normalizeDecimalValue(String(value));
      if (normalized) dtoValues[dtoField] = Number(normalized);
      return;
    }

    if (BOOLEAN_FIELDS.has(dtoField as keyof TStateAidCertificateDto)) {
      dtoValues[dtoField] = Boolean(value);
      return;
    }

    if (typeof value === 'string' && value.trim()) dtoValues[dtoField] = value.trim();
  });

  dto.receivedAidQuarters = values.receivedAidQuarters.map((quarter) => {
    const amountMmsp = normalizeDecimalValue(quarter.amountMmsp);
    const amountGroup = normalizeDecimalValue(quarter.amountGroup);

    return {
      year: quarter.year,
      quarter: quarter.quarter,
      ...(amountMmsp ? { amountMmsp: Number(amountMmsp) } : {}),
      ...(quarter.periodMmsp.trim() ? { periodMmsp: quarter.periodMmsp.trim() } : {}),
      ...(quarter.institutionMmsp.trim()
        ? { institutionMmsp: quarter.institutionMmsp.trim() }
        : {}),
      ...(amountGroup ? { amountGroup: Number(amountGroup) } : {}),
      ...(quarter.periodGroup.trim() ? { periodGroup: quarter.periodGroup.trim() } : {}),
      ...(quarter.institutionGroup.trim()
        ? { institutionGroup: quarter.institutionGroup.trim() }
        : {}),
    };
  });

  return dto;
};
